import { z } from 'zod';
import {
  PAYMENT_METHODS,
  RECURRING_EXPENSE_FREQUENCIES,
  RECURRING_EXPENSE_STATUSES,
} from '../enums.js';
import type { RECURRING_EXPENSE_TRIGGERS } from '../enums.js';
import { currencySchema } from './organization.js';
import {
  optionalDate,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

/**
 * Recurring Expenses — a rule, not a transaction.
 *
 * "Office Internet, ₹2,500, monthly" is the rule; what it produces, on
 * schedule, is an ordinary `Expense` through the ordinary `ExpensesService` —
 * reusing its validation, its Banking-ledger posting, and its audit trail
 * rather than a parallel expense-creation path that has to be kept in step
 * with the real one.
 */

const money = z.coerce.number().positive('Enter an amount greater than zero').max(1e15);

export const createRecurringExpenseSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  description: optionalText(2000),
  vendor: optionalText(200),
  /** Same system picklist vocabulary as `Expense.category`. */
  category: z.string().trim().min(1).max(80).nullish(),
  amount: money,
  currency: currencySchema.optional(),
  verticals: z.array(z.string().trim().min(1).max(80)).default([]),
  paymentMethod: z.enum(PAYMENT_METHODS).default('OTHER'),
  /** Legacy free-text picklist, kept for parity with `createExpenseSchema.paidFromAccount`. */
  paidFromAccount: z.string().trim().min(1).max(80).nullish(),
  bankAccountId: uuidSchema.nullish(),
  startDate: z.iso.date(),
  /** No more occurrences generate once `nextRunDate` would fall after this. */
  endDate: optionalDate(),
  frequency: z.enum(RECURRING_EXPENSE_FREQUENCIES),
  status: z.enum(RECURRING_EXPENSE_STATUSES).default('ACTIVE'),
  accountId: uuidSchema.nullish(),
  contactId: uuidSchema.nullish(),
  businessManagerId: uuidSchema.optional(),
  notes: optionalText(2000),
});
export type CreateRecurringExpenseInput = z.infer<typeof createRecurringExpenseSchema>;

export const updateRecurringExpenseSchema = toPatchSchema(createRecurringExpenseSchema);
export type UpdateRecurringExpenseInput = z.infer<typeof updateRecurringExpenseSchema>;

export const RECURRING_EXPENSE_SORT_FIELDS = ['name', 'nextRunDate', 'amount', 'createdAt'] as const;

export const listRecurringExpensesQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(RECURRING_EXPENSE_SORT_FIELDS).default('nextRunDate'),
  businessManagerId: businessManagerFilterSchema.optional(),
  status: z.enum(RECURRING_EXPENSE_STATUSES).optional(),
  category: z.string().trim().max(80).optional(),
  vendor: z.string().trim().max(200).optional(),
  bankAccountId: uuidSchema.optional(),
  currency: z.string().trim().length(3).optional(),
  frequency: z.enum(RECURRING_EXPENSE_FREQUENCIES).optional(),
  /** Inclusive range on `nextRunDate`. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type ListRecurringExpensesQuery = z.infer<typeof listRecurringExpensesQuerySchema>;

export interface RecurringExpenseOccurrenceView {
  id: string;
  scheduledFor: string;
  expense: RecordRef | null;
  triggeredBy: (typeof RECURRING_EXPENSE_TRIGGERS)[number];
  createdAt: string;
}

export interface RecurringExpenseSummary {
  id: string;
  name: string;
  vendor: string | null;
  category: string | null;
  amount: string;
  currency: string;
  frequency: (typeof RECURRING_EXPENSE_FREQUENCIES)[number];
  status: (typeof RECURRING_EXPENSE_STATUSES)[number];
  startDate: string;
  endDate: string | null;
  nextRunDate: string;
  bankAccount: RecordRef | null;
  account: RecordRef | null;
  contact: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  verticals: string[];
  createdAt: string;
  updatedAt: string;
}

export interface RecurringExpenseDetail extends RecurringExpenseSummary {
  description: string | null;
  paidFromAccount: string | null;
  paymentMethod: (typeof PAYMENT_METHODS)[number];
  notes: string | null;
  occurrences: RecurringExpenseOccurrenceView[];
}
